import React, { useEffect } from 'react'
import _get from 'lodash/get'
import _isNil from 'lodash/isNil'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { List, Map, fromJS } from 'immutable'
import { useLazyQuery } from '@apollo/client'
import { getBoundActions } from '../../actions/index'
import AdyenDropIn from '../AdyenDropIn/index'
import {
  updateAdyenAuthorization,
  ADYEN_PAYMENT_RESPONSE_CODE_AUTHORIZED,
} from '../../services/adyen/index'
import { USD } from '../../services/currency/index'
import { get as getConfig } from '../../config/index'
import {
  PLAN_ID_MONTHLY,
  PLAN_SKU_MONTHLY,
  PLAN_ID_ONE_WEEK_FREE_TRIAL_TO_MONTHLY,
  PLAN_SKU_ONE_WEEK_FREE_TRIAL_TO_MONTHLY,
} from '../../services/plans/index'
import { GET_PLANS_BY_SKU } from '../../services/plans/query'
import { getPrimary } from '../../services/languages/index'
import { formatBillingPlans } from '../../services/plans/utils'
import Button, { TYPES } from '../Button/index'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

function AdyenTestPage (props) {
  const {
    user,
    auth,
    plans,
    payment,
    userAccount,
    checkout,
    inboundTracking,
    doAuthRenew,
    processCheckoutOrder,
    velocityCheck,
  } = props
  const [
    getPlansDataBySku,
    { data: graphQLPlanData, loading: graphQLPlanDataLoading },
  ] = useLazyQuery(GET_PLANS_BY_SKU)
  const authJwt = auth.get('jwt')
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const emailOptIn = false
  // If the user is logged in, get their currency iso from their account
  // Otherwise get the currency iso from the plans
  const paymentResultCode = payment.getIn(['data', 'payments', 'data', 'resultCode'])
  const paymentIsAuthorized = paymentResultCode === ADYEN_PAYMENT_RESPONSE_CODE_AUTHORIZED
  const velocityCheckToken = velocityCheck.getIn(['data', 'velocityCheck', 'token'])
  const getPaymentInfo = (planId, planSku) => {
    const graphQLPlansList = fromJS(formatBillingPlans(graphQLPlanData))
    const plansList = planGraphQLActive ? graphQLPlansList : plans.getIn(['data', 'plans'], Map()) || Map()
    const chosenPlan = plansList.find((p) => p.get('id') === planId) || Map()
    const currencyIso = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'currencyIso'])
    || chosenPlan.get('currencyIso')
    || USD
    return {
      currencyIso,
      country: payment.getIn(['data', 'payments', 'billingAddress', 'country']) || payment.getIn(['data', 'billingInfo', 'billingAddress', 'country']),
      postalCode: payment.getIn(['data', 'payments', 'billingAddress', 'postalCode']) || payment.getIn(['data', 'billingInfo', 'billingAddress', 'postalCode']),
      productRatePlanId: planId,
      planSku,
      adyen: {
        shopperReference: payment.getIn(['data', 'payments', 'data', 'additionalData', 'recurring.shopperReference']) || user.getIn(['data', 'uuid']) || auth.get('uuid'),
        ...(paymentIsAuthorized) && { recurringDetailReference: payment.getIn(['data', 'payments', 'data', 'additionalData', 'recurring.recurringDetailReference']) },
        cardSummary: payment.getIn(['data', 'payments', 'data', 'additionalData', 'cardSummary']),
        fundingSource: payment.getIn(['data', 'payments', 'data', 'additionalData', 'fundingSource']),
      },
    }
  }
  const checkoutOrderSuccess = checkout.getIn(['orderData', 'success'])

  const onSubmitPurchase = (planId, planSku) => {
    processCheckoutOrder({
      auth,
      paymentInfo: getPaymentInfo(planId, planSku),
      tracking: inboundTracking,
      reactivation: false,
      emailOptIn,
      velocityCheckToken,
    })
  }

  // get the plans for the plan grid
  useEffect(() => {
    if (planGraphQLActive) {
      getPlansDataBySku({
        variables: {
          language: userLanguage,
          skus: [
            PLAN_SKU_MONTHLY,
            PLAN_SKU_ONE_WEEK_FREE_TRIAL_TO_MONTHLY,
          ],
        },
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [])

  useEffect(() => {
    if (checkoutOrderSuccess) {
      const authorizationUuid = payment.getIn(['data', 'payments', 'authorization', 'uuid'])

      doAuthRenew(auth)
      updateAdyenAuthorization({
        auth,
        uuid: authorizationUuid,
        billingAccountCreated: true,
      })
    }
  }, [checkoutOrderSuccess])

  if (!planGraphQLActive && plans.getIn(['data', 'plans'], List()).size === 0) {
    return null
  }

  if (planGraphQLActive && (graphQLPlanDataLoading || _isNil(graphQLPlanData))) {
    return null
  }

  return (
    <div className="adyen-test-page" style={{ margin: '40px auto', maxWidth: '600px' }}>
      <AdyenDropIn
        submitButtonText={'Submit Payment'}
        parentProcessing={false}
        error={false}
        planCost={'11.99'}
      />
      <div style={{ margin: '30px 0px' }}>
        <Button
          type={TYPES.PRIMARY}
          onClick={() => onSubmitPurchase(PLAN_ID_MONTHLY, PLAN_SKU_MONTHLY)}
          className="adyen-test-page__submit"
          stacked
        >
          Purchase Monthly Plan
        </Button>
        <Button
          type={TYPES.PRIMARY}
          onClick={() => onSubmitPurchase(
            PLAN_ID_ONE_WEEK_FREE_TRIAL_TO_MONTHLY,
            PLAN_SKU_ONE_WEEK_FREE_TRIAL_TO_MONTHLY,
          )}
          className="adyen-test-page__submit"
          stacked
        >
          Purchase Free Trial Monthly Plan
        </Button>
      </div>
    </div>
  )
}

export default compose(
  connectRedux(
    (state) => ({
      userAccount: state.userAccount,
      user: state.user,
      auth: state.auth,
      plans: state.plans,
      payment: state.payment,
      checkout: state.checkout,
      inboundTracking: state.inboundTracking,
      velocityCheck: state.velocityCheck,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        processCheckoutOrder: actions.checkout.processCheckoutOrder,
        doAuthRenew: actions.auth.doAuthRenew,
      }
    },
  ),
)(AdyenTestPage)
